/**
 * @file The feature selection component. It returns a view that give user the opportunity to select the products they
 * want to install.
 * @author Haonan Yu(haoyu@microStrategy.com)
 */
import { Form, Divider, Row, Col, Tree, Checkbox } from 'antd';
import React from 'react';
//data & funcs
import {
    featureDependencies,
    featureKey2StateMap,
} from '../../../../Utils/WindowsDataLoader';
import {
    createTree,
    initialMaps,
    checkNode,
    uncheckNode,
    getInitialCheckedKeys,
    selectAll,
    unselectAll,
} from '../../../../Utils/CreateTree';
//component
import ButtonBar from '../../../ButtonBar/MainButtonBar/MainButtonBar';
import SelectAllButtonBar from '../../../ButtonBar/SelectAllButtonBar/SelectAllButtonBar';
import Navigation from '../../../Navigation/Navigation';
import ReferencePicture from '../../../ReferencePicture/ReferencePicture';
import {
    M11_6_09,
    M11_6_06,
    M11_6_03,
    M11_5_12,
    M11_5_09,
    M11_5_06,
    M11_5_03,
    M11_4_12,
    WIN_FEATURESELECTION,
} from '../../../../Utils/Constants';
//css
import 'antd/dist/antd.css';
import '../../../../Utils/CommonCss.css';

const PAGE_NAME = WIN_FEATURESELECTION;

class FeatureSelection extends React.Component {
    /**
     * Contains 5 maps to control the checkbox tree
     * @type {Object}
     */
    maps = {
        /**
         * A map to mapping nodes and their children
         * key: nodeKey, value: [nodeKey, nodeKey, ...]
         * @type {Map}
         */
        childrenMap: new Map(),
        /**
         * A map to mapping nodes and its direct parent
         * key: nodeKey, value: nodeKey
         * @type {Map}
         */
        parentMap: new Map(),

        /**
         * A map to mapping nodes and the products they are depend on
         * key: nodeKey, value: [nodeKey, nodeKey, ...]
         * @type {Map}
         */
        dependencyMap: new Map(),

        /**
         * A map to store the disable status of each node
         * key: nodeKey, value: boolean
         * true, the node will be set as disSelectable,
         * false otherwise
         * @type {Map}
         */
        disSelectableMap: new Map(),

        /**
         *  Mapping the key of TreeNode to states in Win20CustomPageObjects.json
         * the key is the value of 'key' for each TreeNode in FeatureSelection Page
         * the value is the corresponding state of that TreeNode
         * @type {Map}
         */
        feature2StateMap: featureKey2StateMap,
    };

    nextStep = () => {
        if (this.checkedKeys.checked.length > 0) {
            this.props.nextStep();
        } else {
            // if no products selected, show error message
            this.showErrorWarning();
            this.forceUpdate();
        }
    };

    backStep = () => {
        this.props.backStep();
    };

    /**
     * Show the Error Warning due to no product selected
     */
    showErrorWarning = () => {
        this.featureContainerStyle = [
            'featureScrollableContainer',
            'featureScrollableContainerError',
        ];
        this.btnBarStyle = 'selectBtnBarError';
        this.showErrorMsg = true;
    };

    /**
     * Remove the Error Warning for "no product selected"
     */
    removeErrorWarning = () => {
        this.featureContainerStyle = 'featureScrollableContainer';
        this.btnBarStyle = 'selectBtnBar';
        this.showErrorMsg = false;
    };

    /**
     * To handle the special cases for the installation on Windows Platform when do the checking action
     * @param {String} nodeKey the unique id of the TreeNode
     * @param {Array} checkedKeys the array that 'antd' treeSelect use to control the tree
     */
    m2021SpecialCheckingCaseHandler = (nodeKey, checkedKeys) => {
        // 1.1, 1.2, 1.7, 1.8 are required if any of 1.x checked
        if (nodeKey.match(/^1$|^1\..$/)) {
            if (!checkedKeys.checked.includes('1.1')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.1');
            }
            if (!checkedKeys.checked.includes('1.2')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.2');
            }
            if (!checkedKeys.checked.includes('1.7')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.7');
            }
            if (!checkedKeys.checked.includes('1.8')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.8');
            }
            this.maps.disSelectableMap.set('1.1', true);
            this.maps.disSelectableMap.set('1.2', true);
            this.maps.disSelectableMap.set('1.7', true);
            this.maps.disSelectableMap.set('1.8', true);
        } else if (nodeKey === '9.3') {
            if (checkedKeys.checked.includes('9.3.1')) {
                checkedKeys.checked.splice(
                    checkedKeys.checked.indexOf('9.3.1'),
                    1
                );
                this.props.setOriginState(
                    featureKey2StateMap.get('9.3.1'),
                    false
                );
            }
        } else if (nodeKey === '3') {
            //Special case for remote repo
            this.props.setOriginState('showRemoteRepoPage', false);
            this.props.setOriginState('remoteRepositoryHostname', '');
            this.props.setOriginState('remoteRepositoryPort', '');
            this.props.setOriginState('remoteRepositoryUsername', '');
            this.props.setOriginState('remoteRepositoryPassword', '');
            this.props.setOriginState('overrideExistingRepositoryPA', '');
            this.props.setOriginState('overrideExistingRepositoryCollab', '');
            this.props.setOriginState('overrideExistingRepositoryStorageService', '');
        } else if (nodeKey === '4.2' || nodeKey === '4' || nodeKey === '18') {
            //Special case for remote repo
            if (!checkedKeys.checked.includes('3')) {
                this.props.setOriginState('showRemoteRepoPage', true);
            }
        }
        // 2, 7, 14 has internal dependencies, but no external dependency.
        this.maps.disSelectableMap.set('2', false);
        this.maps.disSelectableMap.set('9', false);
        this.maps.disSelectableMap.set('16', false);
    };

    /**
     * To handle the special cases for the installation on Windows Platform when do the unChecking action
     * @param {String} nodeKey the unique id of the TreeNode
     * @param {Object} checkedKeys the object that 'antd' treeSelect use to control the tree
     */
    m2021SpecialUnCheckingCaseHandler = (nodeKey, checkedKeys) => {
        if (nodeKey === '1') {
            this.maps.disSelectableMap.set('1.1', false);
            this.maps.disSelectableMap.set('1.2', false);
            this.maps.disSelectableMap.set('1.7', false);
            this.maps.disSelectableMap.set('1.8', false);
        } else if (nodeKey === '9.3.1') {
            // Architect and Function Plugin Special
            if (!checkedKeys.checked.includes('9.3')) {
                checkedKeys.checked.push('9.3');
                this.props.setOriginState(featureKey2StateMap.get('9.3'), true);
            }
            if (!checkedKeys.checked.includes('9')) {
                // 9 has no corresponding state
                checkedKeys.checked.push('9');
            }
        } else if (nodeKey === '3') {
            //Special case for remote repo
            if (
                checkedKeys.checked.includes('4.2') ||
                checkedKeys.checked.includes('18')
            ) {
                this.props.setOriginState('showRemoteRepoPage', true);
            }
        } else if (nodeKey === '4.2' || nodeKey === '4' || nodeKey === '18') {
            //Special case for remote repo
            if (!checkedKeys.checked.includes('3')) {
                if (
                    !checkedKeys.checked.includes('4.2') &&
                    !checkedKeys.checked.includes('18')
                ) {
                    this.props.setOriginState('showRemoteRepoPage', false);
                    this.props.setOriginState('remoteRepositoryHostname', '');
                    this.props.setOriginState('remoteRepositoryPort', '');
                    this.props.setOriginState('remoteRepositoryUsername', '');
                    this.props.setOriginState('remoteRepositoryPassword', '');
                    this.props.setOriginState('overrideExistingRepositoryPA', '');
                    this.props.setOriginState('overrideExistingRepositoryCollab', '');
                    this.props.setOriginState('overrideExistingRepositoryStorageService', '');
                }
            }
        }
    };

    /**
     * event triggered when checkbox is being checked or unchecked.
     * if the box is being checked, make all of its children and dependencies checked. The dependencies should be disSelectable unless the root node has been unchecked
     * if the box is being unchecked, uncheck all of its children. Set its dependencies selectable
     * @event
     * @param {Object} checkedKeys the object that 'antd' treeSelect use to control the UI
     * @param {string} info  the information of the node that trigger this event
     */
    onCheck = (checkedKeys, info) => {
        let nodeKey = info.node.props.eventKey;
        if (info.checked === true) {
            checkNode(nodeKey, checkedKeys, this.maps, this.props);
            this.specialCheckingCaseHandler(nodeKey, checkedKeys);
        } else {
            uncheckNode(nodeKey, checkedKeys, this.maps, this.props);
            this.specialUnCheckingCaseHandler(nodeKey, checkedKeys);
        }

        /*If none products checked and the error message showed, the next
          onCheck call must be the user checked something, so remove the error*/
        if (this.showErrorMsg) {
            this.removeErrorWarning();
        }
    };

    constructor(props) {
        super(props);
        switch (props.useVersion) {
            case M11_4_12:
            case M11_5_03:
            case M11_5_06:
            case M11_5_09:
            case M11_5_12:
            case M11_6_03:
            case M11_6_06:
            case M11_6_09:
                this.specialCheckingCaseHandler =
                    this.m2021SpecialCheckingCaseHandler;
                this.specialUnCheckingCaseHandler =
                    this.m2021SpecialUnCheckingCaseHandler;
                break;
            default:
        }
        // An object to control the status of checkboxes in the tree
        this.checkedKeys = {};
        this.checkedKeys.checked = [];
        initialMaps(featureDependencies, '', this.maps); // default root key ''
        getInitialCheckedKeys(
            this.checkedKeys,
            this.maps,
            this.specialCheckingCaseHandler,
            this.props
        );

        this.featureContainerStyle = 'featureScrollableContainer';
        this.btnBarStyle = 'selectBtnBar';
        // Control error message when non product selected and tried to go next
        this.showErrorMsg = false;
    }

    render() {
        let downloadOpenSourceSoftware = this.props.getOriginState(
            'mySQLSelect'
        ) ? (
            <div>
                <br />
                <b>
                    Option1(Target Machine Internet Connection Required):
                </b>{' '}
                Check the box to agree to download the installation files on
                your target machine
                <br />
                <Checkbox
                    onChange={(e) => {
                        this.props.setOriginState(
                            'agreeToDownloadOpenSourceSoftware',
                            e.target.checked
                        );
                    }}
                    checked={this.props.getOriginState(
                        'agreeToDownloadOpenSourceSoftware'
                    )}
                >
                    Agree to download Open Source Software
                </Checkbox>
                <br />
                <br />
                <p>
                    <b>
                        Option2(Target Machine No Internet Connection Required):
                    </b>{' '}
                    Follow the instructions{' '}
                    <a
                        href="https://community.microstrategy.com/s/article/Download-Open-Source-Software-for-Windows-Installation"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>{' '}
                    to manually download the installation files, and save them
                    to your 'C:\Users\[Username]\Downloads' folder.
                </p>
            </div>
        ) : null;

        return (
            <Form className="form">
                <Row type="flex" align="top">
                    <Col
                        span={4}
                        className={['colNav', 'winFeatureSelectNavHeight'].join(
                            ' '
                        )}
                    >
                        <p className="headlineTop">Steps</p>
                        <Navigation
                            setOriginState={this.props.setOriginState}
                            getOriginState={this.props.getOriginState}
                            steps={this.props.steps}
                            deepestStep={this.props.deepestStep}
                            useOs={this.props.useOs}
                        />
                    </Col>

                    <Col span={12} className="colContent">
                        <p className="headlineTop">Feature Selection</p>
                        <Tree
                            checkedKeys={this.checkedKeys}
                            className={this.featureContainerStyle}
                            checkable
                            checkStrictly
                            onCheck={(checkedKeys, e) =>
                                this.onCheck(this.checkedKeys, e)
                            }
                        >
                            {createTree(
                                featureDependencies,
                                this.maps.disSelectableMap
                            )}
                        </Tree>
                        <SelectAllButtonBar
                            styleName={this.btnBarStyle}
                            showErrorMsg={this.showErrorMsg}
                            selectAll={() => {
                                selectAll(
                                    this.checkedKeys,
                                    this.maps,
                                    this.specialCheckingCaseHandler,
                                    this.props
                                );
                                // remove warning if exists
                                if (this.showErrorMsg) {
                                    this.removeErrorWarning();
                                }
                            }}
                            unselectAll={() => {
                                unselectAll(
                                    this.checkedKeys,
                                    this.maps,
                                    this.specialUnCheckingCaseHandler,
                                    this.props
                                );
                            }}
                        />
                        <div className="info-tag">
                            <img
                                className="info-icon"
                                alt="info icon"
                                src={require('../../../../assets/Icons/info.svg').default}
                            />
                            The Destination folder cannot be manipulated by
                            response file so far!
                        </div>
                        {downloadOpenSourceSoftware}
                    </Col>

                    <Divider
                        className="divider"
                        type="vertical"
                        orientation="right"
                    />
                    <Col span={7} className="colPics">
                        <p className="headlineTop">Reference Pictures</p>
                        <ReferencePicture
                            pageName={PAGE_NAME}
                            getOriginState={this.props.getOriginState}
                        />
                    </Col>
                </Row>
                <ButtonBar
                    useOs={this.props.useOs}
                    clickNext={this.nextStep}
                    clickBack={this.backStep}
                />
            </Form>
        );
    }
}

export default FeatureSelection;
